import {
  Link, Outlet, createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/fetcher";
import { getGetMeQueryOptions, type Me, type Role } from "@/api/generated";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { LoginPage } from "@/pages/LoginPage";

type Ctx = { queryClient: QueryClient };

function NotFound() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-24 text-center">
      <p className="text-sm font-medium">Page not found</p>
      <p className="text-sm text-muted-foreground">It may have moved, or it is not in your scope.</p>
      <Button asChild variant="outline" size="sm"><Link to="/">Back to the ops view</Link></Button>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<Ctx>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => (typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
  component: LoginPage,
});

async function currentUser(qc: QueryClient, href: string): Promise<Me> {
  try {
    return await qc.ensureQueryData(getGetMeQueryOptions({ query: { staleTime: Infinity, retry: false } }));
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      throw redirect({ to: "/login", search: { redirect: href } });
    }
    throw e;
  }
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => ({ me: await currentUser(context.queryClient, location.href) }),
  component: AppShell,
});

// Pages a role may not open send it home (the API refuses them anyway:
// docs/adr/2026-09-28-customer-tenancy.md).
const only = (...roles: Role[]) => ({ context }: { context: { me: Me } }) => {
  if (!roles.includes(context.me.role)) throw redirect({ to: "/" });
};
const adminOnly = only("ADMIN");
const internalOnly = only("ADMIN", "NOC");

function page<P extends string>(path: P, component: ReturnType<typeof lazyRouteComponent>, opts: { admin?: boolean; internal?: boolean } = {}) {
  const guard = opts.admin ? adminOnly : opts.internal ? internalOnly : undefined;
  return createRoute({ getParentRoute: () => appRoute, path, component, ...(guard ? { beforeLoad: guard } : {}) });
}

const opsRoute = page("/", lazyRouteComponent(() => import("@/pages/OpsPage"), "OpsPage"));
export const incidentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/incidents",
  validateSearch: (s: Record<string, unknown>): { view?: "all" } => (s.view === "all" ? { view: "all" } : {}),
  beforeLoad: internalOnly,
  component: lazyRouteComponent(() => import("@/pages/IncidentsPage"), "IncidentsPage"),
});
const incidentRoute = page("/incidents/$incidentId", lazyRouteComponent(() => import("@/pages/IncidentDetailPage"), "IncidentDetailPage"), { internal: true });
const monitorsRoute = page("/monitors", lazyRouteComponent(() => import("@/pages/MonitorsPage"), "MonitorsPage"));
const monitorRoute = page("/monitors/$monitorId", lazyRouteComponent(() => import("@/pages/MonitorDetailPage"), "MonitorDetailPage"));
const probesRoute = page("/probes", lazyRouteComponent(() => import("@/pages/ProbesPage"), "ProbesPage"), { internal: true });
const addProbeRoute = page("/probes/add", lazyRouteComponent(() => import("@/pages/AddProbePage"), "AddProbePage"), { admin: true });
const probeRoute = page("/probes/$probeId", lazyRouteComponent(() => import("@/pages/ProbeDetailPage"), "ProbeDetailPage"), { internal: true });
const linksRoute = page("/links", lazyRouteComponent(() => import("@/pages/LinksPage"), "LinksPage"));
const networkRoute = page("/network", lazyRouteComponent(() => import("@/pages/NetworkPage"), "NetworkPage"));
// ?at=<level>:<path> while browsing, ?lock= when locked, ?mode=wall for wall
// displays (docs/adr/2026-09-28-3d-topology.md).
type TopologySearch = { view?: "3d" | "explorer"; at?: string; lock?: string; link?: string; mode?: "wall" };
export const topologyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/topology",
  validateSearch: (s: Record<string, unknown>): TopologySearch => ({
    ...(s.view === "3d" || s.view === "explorer" ? { view: s.view } : {}),
    ...(typeof s.at === "string" && s.at ? { at: s.at } : {}),
    ...(typeof s.lock === "string" && s.lock ? { lock: s.lock } : {}),
    ...(typeof s.link === "string" && s.link ? { link: s.link } : {}),
    ...(s.mode === "wall" ? { mode: "wall" as const } : {}),
  }),
  beforeLoad: internalOnly,
  component: lazyRouteComponent(() => import("@/pages/TopologyPage"), "TopologyPage"),
});
type MaintenanceSearch = { target_type?: "MONITOR" | "LINK" | "NODE" | "PROBE" | "BGP_PREFIX" | "BGP_ASN"; target_id?: string };
export const maintenanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/maintenance",
  validateSearch: (s: Record<string, unknown>): MaintenanceSearch =>
    typeof s.target_id === "string" && ["MONITOR", "LINK", "NODE", "PROBE", "BGP_PREFIX", "BGP_ASN"].includes(String(s.target_type))
      ? { target_type: s.target_type as MaintenanceSearch["target_type"], target_id: s.target_id }
      : {},
  beforeLoad: internalOnly,
  component: lazyRouteComponent(() => import("@/pages/MaintenancePage"), "MaintenancePage"),
});
// The selection lives in the URL so a report can be shared
// (docs/adr/2026-09-28-sla-reports.md).
type ReportsSearch = { month?: string; tz?: string; tenant?: string };
export const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    ...(typeof s.month === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.month) ? { month: s.month } : {}),
    ...(typeof s.tz === "string" && s.tz ? { tz: s.tz } : {}),
    ...(typeof s.tenant === "string" && s.tenant ? { tenant: s.tenant } : {}),
  }),
  component: lazyRouteComponent(() => import("@/pages/ReportsPage"), "ReportsPage"),
});
const notificationsRoute = page("/notifications", lazyRouteComponent(() => import("@/pages/NotificationsPage"), "NotificationsPage"), { admin: true });
const settingsRoute = page("/settings", lazyRouteComponent(() => import("@/pages/SettingsPage"), "SettingsPage"), { admin: true });
const auditRoute = page("/audit", lazyRouteComponent(() => import("@/pages/AuditPage"), "AuditPage"), { admin: true });
const bgpRoute = page("/bgp", lazyRouteComponent(() => import("@/pages/BgpPage"), "BgpPage"), { internal: true });

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    opsRoute, incidentsRoute, incidentRoute, monitorsRoute, monitorRoute, probesRoute, addProbeRoute, probeRoute,
    linksRoute, networkRoute, topologyRoute, maintenanceRoute, reportsRoute, notificationsRoute, settingsRoute, auditRoute,
    bgpRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultPendingMs: 300,
    // Every search value in the app is a plain string. Keep them that way (no
    // JSON), and leave ":" and "/" readable so bookmarked views look like the
    // PRD's /topology?lock=dc:cgk-dc3/hall-core.
    parseSearch: (str) => Object.fromEntries(new URLSearchParams(str)),
    stringifySearch: (search) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(search)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
      const q = p.toString().replace(/%3A/gi, ":").replace(/%2F/gi, "/");
      return q ? `?${q}` : "";
    },
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
